import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { arrival } from "../lib/arrival";
import { useAuth } from "../context/AuthContext";
import { useAuthModal } from "../context/AuthModalContext";
import Button from "../components/ui/Button";
import MomoPhoneField from "../components/MomoPhoneField";
import { cancellationTerms, formatDay, formatDuration, formatPrice, todayISO } from "../lib/utils";

const STEPS = ["staff", "datetime", "notes", "confirm"];

// A guest's chosen time is kept under this until they sign in to confirm.
// Remembered for the tab, so reloading the page doesn't lock them out of
// their own time.
const GUEST_TOKEN = (() => {
  const fresh = () =>
    window.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  try {
    const saved = sessionStorage.getItem("shuwa_hold_token") || fresh();
    sessionStorage.setItem("shuwa_hold_token", saved);
    return saved;
  } catch {
    return fresh();
  }
})();

// Counts down the minutes the chosen time is kept for the customer.
function HoldNotice({ endsAt, time }) {
  const [left, setLeft] = useState(() => Math.max(0, Math.round((endsAt - Date.now()) / 1000)));

  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((endsAt - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [endsAt]);

  return (
    <p className="mt-3 text-xs text-muted-foreground">
      {left > 0
        ? `We're keeping ${time} for you for ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")} while you confirm — nobody else can book it.`
        : "We're no longer keeping this time for you. You can still confirm if nobody has taken it."}
    </p>
  );
}

// Why a day has no times when nobody works it: "Zuri Hair Studio is closed
// on Sundays." — or, for one date only, "… is closed on Sun 18 Oct 2026."
function closedMessage(weekly, date, staffName, businessName) {
  const weekday = new Date(`${date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long" });
  const when = weekly ? `${weekday}s` : formatDay(date);
  return staffName
    ? `${staffName} doesn't work on ${when}. Pick another day, or choose "Any professional".`
    : `${businessName} is closed on ${when}. Please pick another day.`;
}

export default function BookingFlow() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const serviceIds = useMemo(() => (params.get("services") || "").split(",").filter(Boolean), [params]);

  const [business, setBusiness] = useState(null);
  const [step, setStep] = useState(0);
  const [staffId, setStaffId] = useState(null);
  const [date, setDate] = useState(() => todayISO());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [notes, setNotes] = useState("");
  const [provider, setProvider] = useState("cash");
  const [momoPhone, setMomoPhone] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [mobileMoney, setMobileMoney] = useState({ mtn: true, airtel: true });
  const [walletBalance, setWalletBalance] = useState(null);
  const [testOutcome, setTestOutcome] = useState("success");
  const [feePercent, setFeePercent] = useState(4.5);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [booked, setBooked] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  // The date the customer joined the waitlist for, if any.
  const [waitlistedFor, setWaitlistedFor] = useState(null);
  // What the shop's hours say about the picked date: { open, closedWeekly }.
  const [dayInfo, setDayInfo] = useState(null);
  const [holdEndsAt, setHoldEndsAt] = useState(null);

  useEffect(() => {
    api.get(`/businesses/${slug}`).then(({ data }) => setBusiness(data));
  }, [slug]);

  // Signed-in customers can pay from their SHUWA wallet balance.
  useEffect(() => {
    if (!user) return setWalletBalance(null);
    api
      .get("/wallet")
      .then(({ data }) => setWalletBalance(Number(data.balance)))
      .catch(() => setWalletBalance(null));
  }, [user]);

  useEffect(() => {
    api
      .get("/payments/config")
      .then(({ data }) => {
        setTestMode(Boolean(data.test_mode));
        setFeePercent(Number(data.booking_fee_percent ?? 4.5));
        setMobileMoney(data.mobile_money || { mtn: true, airtel: true });
      })
      .catch(() => setTestMode(false));
  }, []);

  useEffect(() => {
    if (step !== 1 || !business) return;
    const selectedServices = business.services.filter((s) => serviceIds.includes(s.id));
    const duration = selectedServices.reduce((sum, s) => sum + s.duration_minutes, 0);
    let stale = false;
    const load = () =>
      api
        .get(`/businesses/${slug}/availability`, {
          params: { date, staff_id: staffId || undefined, duration_minutes: duration || 30, service_ids: serviceIds, guest_token: GUEST_TOKEN },
        })
        .then(({ data }) => {
          if (stale) return;
          const open = [...new Set(data.staff.flatMap((s) => s.slots))].sort();
          setSlots(open);
          setDayInfo({ open: data.open !== false, closedWeekly: Boolean(data.closed_weekly) });
          setTime((current) => (current && !open.includes(current) ? null : current));
        })
        .catch(() => !stale && setDayInfo((current) => current || { open: true, closedWeekly: false }));
    load();
    // Reloaded every minute, so times that pass or get booked while the page is open drop off.
    const timer = setInterval(load, 60000);
    return () => {
      stale = true;
      clearInterval(timer);
    };
  }, [step, business, date, staffId, serviceIds, slug]);

  // From the moment the customer moves on with a time, it is kept for them
  // and nobody else can book it. Going back to the picker lets it go;
  // otherwise it runs out on its own.
  const bookedId = booked?.id;
  const pastPicker = step > STEPS.indexOf("datetime");
  useEffect(() => {
    if (!pastPicker || !time || bookedId) return;
    let left = false;
    let holdId = null;
    // Asked for a tick later: in development React runs this effect twice,
    // and the first run is then cancelled before its request is ever sent.
    const start = setTimeout(() => {
      api
        .post("/slot-holds", {
          business_slug: slug,
          staff_id: staffId,
          service_ids: serviceIds,
          appointment_date: date,
          start_time: time,
          guest_token: GUEST_TOKEN,
        })
        .then(({ data }) => {
          if (left) return api.delete(`/slot-holds/${data.id}`, { params: { guest_token: GUEST_TOKEN } }).catch(() => {});
          holdId = data.id;
          setHoldEndsAt(Date.now() + data.expires_in_seconds * 1000);
        })
        .catch((e) => {
          // Taken or passed meanwhile: back to the picker, which reloads what's still open.
          if (left || ![409, 422].includes(e?.response?.status)) return;
          setError(e.response.data?.message || "That time is no longer available. Please pick another.");
          setTime(null);
          setStep(STEPS.indexOf("datetime"));
        });
    }, 0);
    return () => {
      left = true;
      clearTimeout(start);
      setHoldEndsAt(null);
      if (holdId) api.delete(`/slot-holds/${holdId}`, { params: { guest_token: GUEST_TOKEN } }).catch(() => {});
    };
  }, [pastPicker, user, time, date, staffId, serviceIds, slug, bookedId]);

  if (!business) return <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Loading…</div>;

  const selectedServices = business.services.filter((s) => serviceIds.includes(s.id));
  // A service assigned to particular staff is done only by them.
  const eligibleStaff = (business.staff || []).filter((member) =>
    selectedServices.every((s) => !s.staff?.length || s.staff.some((x) => x.id === member.id))
  );
  // A shop starts with one staff member made from its owner's sign-up, so
  // "Any professional" is the only choice until it has a team to pick from.
  const staffChoices = eligibleStaff.length > 1 ? eligibleStaff : [];
  const servicesTotal = selectedServices.reduce((sum, s) => sum + Number(s.price), 0);
  const depositTotal = selectedServices.reduce((sum, s) => sum + Number(s.deposit_amount || 0), 0);
  const cancellationFeeTotal = selectedServices.reduce((sum, s) => sum + Number(s.cancellation_fee || 0), 0);
  const freeCancellationHours = selectedServices.length
    ? Math.min(...selectedServices.map((s) => Number(s.free_cancellation_hours ?? 24)))
    : 24;
  // A booking that needs a deposit is only confirmed once it's paid online,
  // so "Pay at store" isn't offered for it.
  // A suspended or rejected shop is paid in person only.
  const online = business.online_payments_enabled !== false;
  const paymentOptions = [
    ...(depositTotal > 0 && online ? [] : [{ id: "cash", label: "Pay at store" }]),
    ...(online && mobileMoney.mtn ? [{ id: "mtn", label: "MTN Mobile Money" }] : []),
    ...(online && mobileMoney.airtel ? [{ id: "airtel", label: "Airtel Money" }] : []),
    ...(online && walletBalance !== null ? [{ id: "wallet", label: `SHUWA wallet (${formatPrice(walletBalance)})` }] : []),
    ...(online && testMode ? [{ id: "test", label: "Test payment" }] : []),
  ];
  const chosenProvider = paymentOptions.some((p) => p.id === provider) ? provider : paymentOptions[0]?.id;
  // SHUWA's booking fee, added on top and paid by the customer (the
  // server computes the charged amount the same way). Paying at the store
  // carries no fee for the customer.
  const serviceFee = chosenProvider === "cash" ? 0 : Math.round((servicesTotal * feePercent) / 100);
  const total = servicesTotal + serviceFee;

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    if (!chosenProvider) {
      setError("This booking needs a deposit paid online, and no online payment method is available right now. Please contact the business.");
      setSubmitting(false);
      return;
    }

    try {
      const { data } = await api.post("/appointments", {
        business_slug: slug,
        staff_id: staffId,
        service_ids: serviceIds,
        appointment_date: date,
        start_time: time,
        guest_token: GUEST_TOKEN,
        notes,
        source: location.state?.source === "marketplace" ? "marketplace" : "direct",
        // The search that listed this shop, so SHUWA can count which
        // searches lead to bookings. Not saved with the booking.
        search_term: location.state?.searchTerm || undefined,
        // Where this visit started (a Google result, a shared link…).
        ...arrival(),
      });

      try {
        const { data: paymentData } = await api.post(`/appointments/${data.id}/pay`, {
          provider: chosenProvider,
          payment_type: depositTotal > 0 ? "deposit" : "full",
          phone: ["mtn", "airtel"].includes(chosenProvider) && momoPhone ? momoPhone : undefined,
          test_outcome: chosenProvider === "test" ? testOutcome : undefined,
        });

        setPaymentMessage(paymentData.message);
      } catch (payErr) {
        setPaymentMessage(
          payErr?.response?.data?.message ||
            (depositTotal > 0
              ? "The deposit could not be processed. Pay it from your appointments page to confirm this booking."
              : "Payment could not be processed — you can pay at the store.")
        );
      }
      // Show what the booking actually is now: confirmed, or still waiting.
      const latest = await api.get(`/appointments/${data.id}`).then((r) => r.data).catch(() => data);
      setBooked(latest);
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong. Please try another slot.");
      // The time was taken meanwhile: back to the picker, which reloads what's still open.
      if (e?.response?.status === 409) {
        setTime(null);
        setStep(STEPS.indexOf("datetime"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const joinWaitlist = async () => {
    if (!user) {
      openAuthModal("register");
      return;
    }
    setJoiningWaitlist(true);
    try {
      await api.post("/waitlist", {
        business_slug: slug,
        service_id: serviceIds[0],
        staff_id: staffId,
        date,
        // Any time that opens on the picked date.
        any_time_today: true,
        notify_immediately: true,
      });
      setWaitlistedFor(date);
    } finally {
      setJoiningWaitlist(false);
    }
  };

  if (booked) {
    const confirmed = booked.status === "confirmed";
    const where = [business.address, business.city].filter(Boolean).join(", ");
    const rows = [
      ["Business", business.name],
      ["Service", (booked.services || []).map((s) => s.name_snapshot).join(", ")],
      ["When", `${formatDay(booked.appointment_date)} at ${booked.start_time?.slice(0, 5)}`],
      ...(booked.staff?.name ? [["With", booked.staff.name]] : []),
      ...(where ? [["Where", where]] : []),
      ["Price", formatPrice(booked.total_amount)],
      ...(Number(booked.amount_paid) > 0 ? [["Paid", formatPrice(booked.amount_paid)]] : []),
      ["Status", confirmed ? "Confirmed" : "Waiting for confirmation"],
    ];
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <div
          className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full text-3xl ${
            confirmed ? "bg-emerald-100" : "bg-amber-100"
          }`}
        >
          {confirmed ? "✓" : "…"}
        </div>
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">
          {confirmed ? "You're booked!" : "Booking received — not confirmed yet"}
        </h1>
        {!confirmed && (
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
            {depositTotal > 0
              ? `Pay the ${formatPrice(depositTotal)} deposit from your appointments page to confirm it. Unpaid bookings are released after 30 minutes.`
              : `${business.name} still has to confirm it. We'll email you as soon as they do.`}
          </p>
        )}
        {paymentMessage && (
          <p className="mx-auto mt-3 max-w-sm rounded-xl bg-accent p-3 text-xs text-accent-foreground">
            {paymentMessage}
          </p>
        )}
        <div className="mt-5 space-y-2 rounded-2xl border border-border p-4 text-left text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right font-medium text-slate-900">{value}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-left text-xs text-muted-foreground">
          {cancellationTerms(freeCancellationHours, cancellationFeeTotal)}
        </p>
        <Button className="mt-6" onClick={() => navigate("/account/appointments")}>
          View my appointments
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pt-8 pb-4 sm:px-6">
      <button
        onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))}
        className="mb-4 flex items-center gap-1 text-sm text-muted-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-6 rounded-2xl border border-border bg-white p-4">
        <p className="text-sm text-muted-foreground">{business.name}</p>
        <div className="mt-1 flex flex-wrap justify-between gap-2 text-sm">
          <span>{selectedServices.map((s) => s.name).join(", ")}</span>
          <span className="font-semibold text-slate-900">{formatPrice(servicesTotal)}</span>
        </div>
      </div>

      {STEPS[step] === "staff" && (
        <div>
          <h2 className="mb-4 font-montserrat text-lg font-bold">Choose a professional</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setStaffId(null)}
              className={`rounded-xl border p-3 text-sm ${!staffId ? "border-primary bg-primary/5" : "border-border"}`}
            >
              Any professional
            </button>
            {staffChoices.map((s) => (
              <button
                key={s.id}
                onClick={() => setStaffId(s.id)}
                className={`rounded-xl border p-3 text-sm ${staffId === s.id ? "border-primary bg-primary/5" : "border-border"}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {STEPS[step] === "datetime" && (
        <div>
          <h2 className="mb-4 font-montserrat text-lg font-bold">Pick a date & time</h2>
          <input
            type="date"
            value={date}
            min={todayISO()}
            onChange={(e) => {
              // iPhones ignore "min" on date inputs, so past days are turned away here.
              const picked = e.target.value;
              setDate(picked && picked >= todayISO() ? picked : todayISO());
              setTime(null);
              setSlots([]);
              setDayInfo(null);
            }}
            className="mb-4 h-11 rounded-xl border border-border px-4 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {slots.length ? (
              slots.map((t) => (
                <button
                  key={t}
                  onClick={() => {
                    setTime(t);
                    setError(null);
                  }}
                  className={`rounded-full border px-4 py-2 text-sm ${time === t ? "border-primary bg-primary text-white" : "border-border"}`}
                >
                  {t}
                </button>
              ))
            ) : !dayInfo ? (
              <p className="text-sm text-muted-foreground">Loading times…</p>
            ) : !dayInfo.open ? (
              <p className="text-sm text-muted-foreground">
                {closedMessage(dayInfo.closedWeekly, date, eligibleStaff.find((s) => s.id === staffId)?.name, business.name)}
              </p>
            ) : waitlistedFor === date ? (
              <p className="rounded-xl bg-accent p-3 text-xs text-accent-foreground">
                You're on the waitlist for {formatDay(date)} — we'll notify you the moment a time opens.
              </p>
            ) : (
              <div>
                <p className="mb-3 text-sm text-muted-foreground">No open times left on {formatDay(date)}.</p>
                <Button variant="secondary" disabled={joiningWaitlist} onClick={joinWaitlist}>
                  {joiningWaitlist ? "Joining…" : `Notify me if a time opens on ${formatDay(date)}`}
                </Button>
              </div>
            )}
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </div>
      )}

      {STEPS[step] === "notes" && (
        <div>
          <h2 className="mb-4 font-montserrat text-lg font-bold">Anything the business should know?</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            placeholder="Add a note (optional)"
            className="w-full rounded-xl border border-border p-4 text-sm outline-none focus:border-primary"
          />
        </div>
      )}

      {STEPS[step] === "confirm" && (
        <div>
          <h2 className="mb-4 font-montserrat text-lg font-bold">Review & confirm</h2>
          <div className="space-y-3 rounded-2xl border border-border p-4 text-sm">
            <div className="flex justify-between"><span>Date</span><span>{date}</span></div>
            <div className="flex justify-between"><span>Time</span><span>{time}</span></div>
            <div className="flex justify-between"><span>Duration</span><span>{formatDuration(selectedServices.reduce((s, x) => s + x.duration_minutes, 0))}</span></div>
            <div className="flex justify-between"><span>Services</span><span>{formatPrice(servicesTotal)}</span></div>
            {serviceFee > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>SHUWA booking fee ({feePercent}%)</span>
                <span>{formatPrice(serviceFee)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold"><span>Total</span><span>{formatPrice(total)}</span></div>
            {depositTotal > 0 && (
              <>
                <div className="flex justify-between text-primary"><span>Deposit due now</span><span>{formatPrice(depositTotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Due at appointment</span><span>{formatPrice(total - depositTotal)}</span></div>
              </>
            )}
          </div>
          <p className="mt-3 rounded-xl bg-accent p-3 text-xs text-accent-foreground">
            {cancellationTerms(freeCancellationHours, cancellationFeeTotal)}
            {depositTotal > 0 && (
              <> This booking is confirmed once the {formatPrice(depositTotal)} deposit is paid.</>
            )}
          </p>

          <div className="mt-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Payment method</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {paymentOptions.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProvider(p.id)}
                  className={`rounded-xl border p-3 text-left text-sm ${chosenProvider === p.id ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {["mtn", "airtel"].includes(chosenProvider) && (
              <MomoPhoneField accountPhone={user?.phone} value={momoPhone} onChange={setMomoPhone} className="mt-3" />
            )}
            {chosenProvider === "test" && (
              <div className="mt-3 flex gap-2">
                {[
                  { id: "success", label: "Approve" },
                  { id: "failed", label: "Decline" },
                ].map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setTestOutcome(o.id)}
                    className={`flex-1 rounded-xl border p-2 text-sm ${testOutcome === o.id ? "border-primary bg-primary/5" : "border-border"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {chosenProvider === "cash"
                ? "Pay in person when you arrive — no SHUWA booking fee."
                : chosenProvider === "mtn"
                ? "You'll get a prompt on your phone from MTN MoMo to approve this payment."
                : chosenProvider === "airtel"
                ? "You'll get a prompt on your phone from Airtel Money to approve this payment."
                : chosenProvider === "wallet"
                ? walletBalance >= total
                  ? "Paid straight from your SHUWA wallet balance."
                  : `Your wallet has ${formatPrice(walletBalance ?? 0)} — add money from Account → Wallet, or choose another method.`
                : "Test mode — settles instantly as approved or declined. No money is moved."}
            </p>
            {chosenProvider !== "cash" && (
              <div className="mt-3 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Pay securely, get peace of mind</p>
                  <p className="mt-1 text-xs text-slate-700">
                    Pay for your appointment with confidence. Your payment is held securely until your appointment is
                    completed, then released to the professional. SHUWA makes payments simple and transparent, so you
                    can focus on enjoying your service.
                  </p>
                </div>
              </div>
            )}
          </div>

          {!user && (
            <p className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm text-slate-700">
              Almost there — create a free SHUWA account (or log in) to confirm and pay. Your
              selections stay as they are.
            </p>
          )}

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </div>
      )}

      {holdEndsAt && <HoldNotice endsAt={holdEndsAt} time={time} />}

      <div className="sticky bottom-0 z-30 mt-8 border-t border-border bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between gap-4 py-3">
          <span className="font-semibold text-slate-900">
            {depositTotal > 0 && STEPS[step] === "confirm" ? `${formatPrice(depositTotal)} due now` : formatPrice(total)}
          </span>
          {STEPS[step] !== "confirm" ? (
            <Button
              disabled={STEPS[step] === "datetime" && !time}
              onClick={() => setStep(step + 1)}
            >
              Next
            </Button>
          ) : (
            user ? (
              <Button onClick={submit} disabled={submitting}>
                {submitting ? "Confirming…" : "Confirm booking"}
              </Button>
            ) : (
              <Button onClick={() => openAuthModal("register")}>Create account to book</Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
